// src/v2/hooks/useTabState.js
//
// OBJECT-TABS-PERSIST-LAST-SELECTION
//
// Shared hook for object-page tab state with URL + localStorage persistence.
//
// Resolution order on mount:
//   1. urlTab  — if present in validTabs, use it (explicit deep-link wins).
//   2. loadLastTab(groupKey, instanceId) — if valid, restore from memory.
//   3. defaultTab — the page's hardcoded fallback.
//
// On setActiveTab(key):
//   • Saves to localStorage via saveLastTab.
//   • Calls onUrlChange(key) so the caller can update the URL (keeps URL
//     concerns out of this hook).
//
// NOTE: Does NOT call useSearchParams — see useSearchParams nav trap in
// memory. The caller must pass urlTab as a one-time mount-read value (plain
// string, not a reactive subscription) to avoid competing subscriptions that
// stall route transitions in React 18.
//
// Uses useRef to read urlTab only at mount, not as a reactive dependency.

import { useCallback, useEffect, useRef, useState } from "react";
import { loadLastTab, saveLastTab } from "../../services/tabStatePersistence.js";

/**
 * @param {object} opts
 * @param {string}   opts.groupKey    – passed to tabStatePersistence (e.g. "location")
 * @param {string}   [opts.instanceId] – per-instance key (locationId, leaseId, …)
 * @param {string}   [opts.urlTab]    – tab value read from URL at mount (NOT reactive)
 * @param {string[]} opts.validTabs   – list of accepted tab keys
 * @param {string}   opts.defaultTab  – fallback when URL+memory both miss
 * @param {function} [opts.onUrlChange] – called with the tab key when setActiveTab fires
 *
 * @returns {[string, function]}  [activeTab, setActiveTab]
 */
export function useTabState({ groupKey, instanceId, urlTab, validTabs, defaultTab, onUrlChange }) {
  // Capture urlTab at mount only — intentionally not in the dependency array
  // of any effect so re-renders never re-initialise from the URL.
  const urlTabRef       = useRef(urlTab);
  const validTabsRef    = useRef(validTabs);
  const defaultTabRef   = useRef(defaultTab);
  const groupKeyRef     = useRef(groupKey);
  const instanceIdRef   = useRef(instanceId);
  const onUrlChangeRef  = useRef(onUrlChange);

  // Keep mutable refs current on each render so callbacks see latest values.
  // urlTabRef must also stay in sync: when the parent re-renders with a new
  // urlTab (e.g. navigating from /offices/A/score → /offices/B with no segment,
  // so urlTab changes from "score" to undefined), the instanceId-change effect
  // needs the fresh value or it will incorrectly apply the stale URL tab to the
  // new instance instead of falling through to localStorage memory.
  onUrlChangeRef.current  = onUrlChange;
  urlTabRef.current       = urlTab;
  validTabsRef.current    = validTabs;
  defaultTabRef.current   = defaultTab;

  const resolveInitialTab = () => {
    const url      = urlTabRef.current;
    const valid    = validTabsRef.current;
    const fallback = defaultTabRef.current;

    // 1. URL wins if it's in the valid set.
    if (url && valid.includes(url)) return url;

    // 2. localStorage memory.
    const remembered = loadLastTab(groupKeyRef.current, instanceIdRef.current);
    if (remembered && valid.includes(remembered)) return remembered;

    // 3. Hardcoded default.
    return fallback;
  };

  const [activeTab, setActiveTabInternal] = useState(resolveInitialTab);

  const setActiveTab = useCallback((key) => {
    setActiveTabInternal((prev) => {
      if (key === prev) return prev;
      // Persist to localStorage.
      saveLastTab(groupKeyRef.current, key, instanceIdRef.current);
      // Notify caller to update URL.
      if (typeof onUrlChangeRef.current === "function") {
        onUrlChangeRef.current(key);
      }
      return key;
    });
  }, []); // intentionally mount-only

  // When instanceId changes (user navigates from one detail page to another
  // in the same mounted component), re-resolve from memory for the new instance.
  // URL still wins if it's valid.
  const prevInstanceIdRef = useRef(instanceId);
  useEffect(() => {
    if (instanceId === prevInstanceIdRef.current) return;
    prevInstanceIdRef.current = instanceId;

    // Update mutable refs for the new instance.
    instanceIdRef.current = instanceId;

    const url      = urlTabRef.current;
    const valid    = validTabsRef.current;
    const fallback = defaultTabRef.current;

    if (url && valid.includes(url)) {
      setActiveTabInternal(url);
    } else {
      const remembered = loadLastTab(groupKey, instanceId);
      setActiveTabInternal(remembered && valid.includes(remembered) ? remembered : fallback);
    }
  }, [groupKey, instanceId]); // groupKey/instanceId are the only reactive values needed here

  return [activeTab, setActiveTab];
}
